import {
  type InvoiceDisplayRow,
  type InvoiceOutput,
  useInvoicesForOrder,
} from '@/api/hooks/useInvoicesForOrder'
import { getMappingDisplay } from '@/api/hooks/useOrderDetails'
import type { DataPerspective } from '@/api/hooks/useOrders'
import { integrationStatusConfig } from '@/pages/Dashboard/panels/OrdersPanel/OrdersCellRenderers'
import { formatCompactDateTime } from '@/utils/dateFormatters'
import { DollarOutlined } from '@ant-design/icons'
import {
  BBDTag,
  GraviGrid,
  Horizontal,
  Texto,
  Vertical,
} from '@gravitate-js/excalibrr'
import type {
  ColDef,
  GetDetailRowDataParams,
  ValueFormatterParams,
} from 'ag-grid-community'
import { useMemo } from 'react'

interface InvoicesGridProps {
  orderId: string
  dataPerspective: DataPerspective
}

interface InvoiceRow {
  id: string
  status: InvoiceDisplayRow['status']
  created_on: string
  number: string
  type: string
  counterparty: string
  sent_on: string
  note: string
  invoice: InvoiceOutput | null
}

interface DetailRow {
  id: string
  type: string
  subtype: string
  product: string
  origin: string
  destination: string
  bol_number: string
  rate: number | null
  amount: number | null
  total: number | null
  uom: string
  net_volume: number | null
  gross_volume: number | null
  delivery_date: string
}

function pickInvoice(
  row: InvoiceDisplayRow,
  perspective: DataPerspective,
): InvoiceOutput | null {
  switch (perspective) {
    case 'gravitate_order':
      return row.master_invoice ?? row.internal_invoice ?? null
    case 'partner_order':
      return row.partner_invoice ?? row.internal_invoice ?? null
    default:
      return row.internal_invoice ?? null
  }
}

function StatusCell({ value }: { value: InvoiceRow['status'] }) {
  const config = integrationStatusConfig[value] ?? {
    color: 'default',
    label: value,
    icon: null,
  }
  return (
    <BBDTag color={config.color}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        {config.icon} {config.label}
      </span>
    </BBDTag>
  )
}

function flattenDetails(invoice: InvoiceOutput | null): DetailRow[] {
  if (!invoice) return []
  const rows: DetailRow[] = []
  for (const txn of invoice.transactions) {
    for (const d of txn.details) {
      rows.push({
        id: d.id ?? `${txn.id}-${rows.length}`,
        type: d.type,
        subtype: d.subtype ?? '-',
        product: getMappingDisplay(d.product),
        origin: getMappingDisplay(d.origin),
        destination: getMappingDisplay(d.destination),
        bol_number: d.bol_number ?? '-',
        rate: d.rate ?? null,
        amount: d.amount ?? null,
        total: d.total ?? null,
        uom: d.uom ?? '-',
        net_volume: d.net_volume ?? null,
        gross_volume: d.gross_volume ?? null,
        delivery_date: d.delivery_date
          ? formatCompactDateTime(d.delivery_date)
          : '-',
      })
    }
  }
  return rows
}

const formatNumber = (
  params: ValueFormatterParams<DetailRow, number | null>,
) => (params.value == null ? '-' : params.value.toLocaleString())

export function InvoicesGrid({ orderId, dataPerspective }: InvoicesGridProps) {
  const { data, isLoading } = useInvoicesForOrder(orderId)

  const rowData = useMemo<InvoiceRow[]>(() => {
    if (!data) return []
    return data.map((row) => {
      const invoice = pickInvoice(row, dataPerspective)
      return {
        id: row.id,
        status: row.status,
        created_on: formatCompactDateTime(row.created_on),
        number: invoice?.number ?? '-',
        type: invoice?.type ?? '-',
        counterparty: getMappingDisplay(invoice?.counterparty),
        sent_on: invoice ? formatCompactDateTime(invoice.sent_on) : '-',
        note: invoice?.note ?? '-',
        invoice,
      }
    })
  }, [data, dataPerspective])

  const columnDefs = useMemo<ColDef<InvoiceRow>[]>(
    () => [
      {
        headerName: 'Invoice #',
        field: 'number',
        minWidth: 160,
        sortable: true,
        filter: true,
        cellRenderer: 'agGroupCellRenderer',
      },
      {
        headerName: 'Type',
        field: 'type',
        minWidth: 110,
        sortable: true,
        filter: true,
      },
      {
        headerName: 'Status',
        field: 'status',
        minWidth: 160,
        sortable: true,
        filter: true,
        cellRenderer: (params: { value: InvoiceRow['status'] }) => (
          <StatusCell value={params.value} />
        ),
      },
      {
        headerName: 'Counterparty',
        field: 'counterparty',
        minWidth: 160,
        sortable: true,
        filter: true,
      },
      {
        headerName: 'Created',
        field: 'created_on',
        minWidth: 180,
        sortable: true,
      },
      {
        headerName: 'Sent',
        field: 'sent_on',
        minWidth: 180,
        sortable: true,
      },
      {
        headerName: 'Note',
        field: 'note',
        minWidth: 200,
        flex: 1,
        sortable: false,
      },
    ],
    [],
  )

  const detailCellRendererParams = useMemo(
    () => ({
      detailGridOptions: {
        columnDefs: [
          { headerName: 'Type', field: 'type', minWidth: 140 },
          { headerName: 'Subtype', field: 'subtype', minWidth: 140 },
          { headerName: 'Product', field: 'product', minWidth: 140 },
          { headerName: 'Origin', field: 'origin', minWidth: 140 },
          { headerName: 'Destination', field: 'destination', minWidth: 140 },
          { headerName: 'BOL #', field: 'bol_number', minWidth: 120 },
          {
            headerName: 'Rate',
            field: 'rate',
            minWidth: 100,
            valueFormatter: formatNumber,
          },
          {
            headerName: 'Amount',
            field: 'amount',
            minWidth: 110,
            valueFormatter: formatNumber,
          },
          {
            headerName: 'Total',
            field: 'total',
            minWidth: 110,
            valueFormatter: formatNumber,
          },
          { headerName: 'UOM', field: 'uom', minWidth: 80 },
          {
            headerName: 'Net',
            field: 'net_volume',
            minWidth: 100,
            valueFormatter: formatNumber,
          },
          {
            headerName: 'Gross',
            field: 'gross_volume',
            minWidth: 100,
            valueFormatter: formatNumber,
          },
          {
            headerName: 'Delivered',
            field: 'delivery_date',
            minWidth: 180,
          },
        ] as ColDef<DetailRow>[],
        defaultColDef: { sortable: true, resizable: true },
        getRowId: (params: { data: DetailRow }) => params.data.id,
        overlayNoRowsTemplate:
          '<span style="color: var(--muted-foreground); font-size: 13px;">No transaction details</span>',
      },
      getDetailRowData: (params: GetDetailRowDataParams<InvoiceRow>) => {
        params.successCallback(flattenDetails(params.data.invoice))
      },
    }),
    [],
  )

  return (
    <Vertical
      style={{
        flex: 1,
        width: '100%',
      }}
    >
      <Horizontal
        alignItems='center'
        justifyContent='space-between'
        style={{ padding: '12px 0px', flexShrink: 0 }}
      >
        <Horizontal gap={6} alignItems='center'>
          <DollarOutlined style={{ fontSize: 14, color: 'var(--gray-600)' }} />
          <Texto
            style={{
              fontSize: 15,
              fontWeight: 600,
              color: 'var(--gray-600)',
            }}
          >
            Invoices
          </Texto>
        </Horizontal>
      </Horizontal>

      <div style={{ flex: 1, minHeight: 0 }}>
        <GraviGrid
          rowData={rowData}
          columnDefs={columnDefs}
          sideBar={false}
          masterDetail
          detailCellRendererParams={detailCellRendererParams}
          agPropOverrides={{
            getRowId: (params) => params.data.id,
            suppressRowClickSelection: true,
            rowGroupPanelShow: 'never',
            detailRowAutoHeight: true,
            defaultColDef: {
              sortable: true,
              resizable: true,
            },
            overlayNoRowsTemplate: isLoading
              ? '<span style="color: var(--muted-foreground); font-size: 13px;">Loading invoices...</span>'
              : '<span style="color: var(--muted-foreground); font-size: 13px;">No invoices recorded</span>',
          }}
        />
      </div>
    </Vertical>
  )
}
